<script setup>
import { computed, defineProps } from 'vue';
import markdownit from 'markdown-it';
import texmath from 'markdown-it-texmath';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import 'markdown-it-texmath/css/texmath.css';

const props = defineProps({
  content: {
    type: String,
    default: '',
  },
});

const createMarkdown = () => {
  const instance = markdownit({
    linkify: true,
    breaks: true,
    html: false,
  });

  instance.use(texmath, {
    engine: katex,
    delimiters: 'dollars',
    katexOptions: {
      throwOnError: false,
    },
  });

  const defaultRender = instance.renderer.rules.link_open || function render(tokens, idx, options, env, self) {
    return self.renderToken(tokens, idx, options);
  };

  instance.renderer.rules.link_open = function linkOpen(tokens, idx, options, env, self) {
    const targetIndex = tokens[idx].attrIndex('target');
    const relIndex = tokens[idx].attrIndex('rel');

    if (targetIndex < 0) {
      tokens[idx].attrPush(['target', '_blank']);
    } else {
      tokens[idx].attrs[targetIndex][1] = '_blank';
    }

    if (relIndex < 0) {
      tokens[idx].attrPush(['rel', 'noopener noreferrer']);
    } else {
      tokens[idx].attrs[relIndex][1] = 'noopener noreferrer';
    }

    return defaultRender(tokens, idx, options, env, self);
  };

  return instance;
};

const markdown = createMarkdown();
const fallbackMarkdown = markdownit({
  linkify: true,
  breaks: true,
  html: false,
});

const renderedContent = computed(() => {
  const content = props.content || '';

  try {
    return markdown.render(content);
  } catch (error) {
    console.warn('COMPOSITE markdown math rendering failed:', error);
    return fallbackMarkdown.render(content);
  }
});
</script>

<template>
  <div class="composite-markdown" v-html="renderedContent"></div>
</template>

<style scoped>
.composite-markdown {
  color: #d6cdc6;
  font-size: 0.82rem;
  line-height: 1.75;
}

.composite-markdown :deep(p) {
  margin: 0 0 0.95rem;
}

.composite-markdown :deep(p:last-child) {
  margin-bottom: 0;
}

.composite-markdown :deep(code) {
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0.45rem;
  background: rgba(255, 255, 255, 0.04);
  color: #f5f1ed;
  padding: 0.15rem 0.4rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
  font-size: 0.76rem;
}

.composite-markdown :deep(.katex) {
  color: #f5f1ed;
  font-size: 1em;
}

.composite-markdown :deep(.katex-display) {
  margin: 1rem 0;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 0.15rem;
}

.composite-markdown :deep(a) {
  color: #ff8a66;
  text-decoration: underline;
  text-decoration-color: rgba(254, 62, 1, 0.3);
  text-underline-offset: 0.2rem;
}

.composite-markdown :deep(a:hover) {
  color: #fff;
}
</style>
